import React, { useCallback } from 'react';
import { Typography, Card, Spin, message } from 'antd';
import {
  InstagramOutlined,
  FacebookOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { snsPostsApi } from '../api/endpoints';
import { PLATFORM_COLORS } from '../constants/status';
import type { SnsPost, Platform } from '../api/types';

const { Title } = Typography;

const PLATFORM_ICONS: Record<Platform, React.ReactNode> = {
  instagram: <InstagramOutlined />,
  line: <MessageOutlined />,
  facebook: <FacebookOutlined />,
};

const CalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: posts, isLoading } = useQuery({
    queryKey: ['sns-posts-calendar'],
    queryFn: () => snsPostsApi.list().then(r => ((r.data as any).data ?? r.data) as SnsPost[]),
  });

  const updateDateMutation = useMutation({
    mutationFn: ({ id, scheduled_date }: { id: number; scheduled_date: string }) =>
      snsPostsApi.update(id, { scheduled_date }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sns-posts-calendar'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      message.success('日付を変更しました');
    },
    onError: () => message.error('日付の変更に失敗しました'),
  });

  const events = (posts || []).map((post: SnsPost) => {
    const platform = post.platform as Platform;
    return {
      id: String(post.id),
      title: post.store?.name || '',
      date: post.scheduled_date,
      backgroundColor: PLATFORM_COLORS[platform],
      borderColor: PLATFORM_COLORS[platform],
      extendedProps: { post, platform },
      editable: post.status !== 'posted',
    };
  });

  const handleEventClick = useCallback((info: any) => {
    navigate(`/posts/${info.event.id}`);
  }, [navigate]);

  const handleEventDrop = useCallback((info: any) => {
    const post = info.event.extendedProps.post as SnsPost;
    if (post.status === 'posted') {
      info.revert();
      message.warning('投稿済のタスクは移動できません');
      return;
    }
    const newDate = info.event.startStr;
    updateDateMutation.mutate({ id: post.id, scheduled_date: newDate });
  }, [updateDateMutation]);

  const renderEventContent = useCallback((eventInfo: any) => {
    const platform = eventInfo.event.extendedProps.platform as Platform;
    const post = eventInfo.event.extendedProps.post as SnsPost;
    const isWeekView = eventInfo.view.type === 'dayGridWeek';

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '1px 4px', overflow: 'hidden' }}>
        <span style={{ fontSize: 14, flexShrink: 0 }}>
          {PLATFORM_ICONS[platform]}
        </span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
          {eventInfo.event.title}
        </span>
      </div>
    );
  }, []);

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>カレンダー</Title>
      <Card>
        {isLoading ? (
          <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
        ) : (
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            locale="ja"
            events={events}
            editable={true}
            eventClick={handleEventClick}
            eventDrop={handleEventDrop}
            eventContent={renderEventContent}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,dayGridWeek',
            }}
            buttonText={{
              today: '今日',
              month: '月',
              week: '週',
            }}
            height="auto"
            dayMaxEvents={false}
            eventDisplay="block"
          />
        )}
      </Card>
    </div>
  );
};

export default CalendarPage;
